import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { navigate } from '../../core/router.js';
import { withProduct } from '../../core/route-map.js';
import { toast } from '../../components/toast.js';
import { skeleton } from '../../components/skeleton.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { empty } from '../../components/empty.js';
import { mountComposer } from '../chat/composer.js';
import { uploadFiles } from '../chat/upload.js';
import { takeAskDraft } from '../knowledge/viewer.js';
import {
  STAGES,
  SUGGESTED,
  connectedGroups,
  filterStages,
  filterSuggested,
  stageSources,
} from './stages.js';

/**
 * @param {{ me: object, productId: string, productName: string }} ctx
 */
export function renderNewChat(ctx) {
  const root = el('section', { class: 'newchat-screen' });
  const body = el('div', { class: 'newchat-screen-body' });
  root.append(body);
  body.append(skeleton(5));
  let slowTimer = setTimeout(() => {
    if (body.isConnected) body.append(el('p', { class: 'thread-empty' }, 'Still loading new chat…'));
  }, 8000);

  const composer = mountComposer({
    placeholder: `Ask about ${ctx.productName || 'this product'}…`,
    onSend: (payload) => send(payload),
  });
  const askDraft = takeAskDraft();
  if (askDraft && askDraft.context) {
    const label = askDraft.label || 'Document';
    composer.prefill(
      `Context: ${label}\n${askDraft.context}\n\nTell me more about this.`,
    );
  }

  async function send(payload, preset) {
    const draft = preset || { text: payload.text, files: payload.files || [] };
    const modelId = (payload && payload.modelId) || 'auto';
    if (!preset) composer.takeDraft();
    if (!draft.text && !(draft.files && draft.files.length)) return;
    if (!ctx.productId) {
      toast('Choose a product first', 'error');
      if (!preset) composer.restore(draft);
      return;
    }
    composer.setBusy(true);
    let attachments = [];
    try {
      if (draft.files && draft.files.length) attachments = await uploadFiles(draft.files);
      const created = await api('/chat', {
        method: 'POST',
        body: { name: 'New conversation', product_id: ctx.productId },
      });
      if (!created || created.error || !created.group) {
        throw new Error((created && created.error) || 'Could not start the chat');
      }
      const sent = await api(`/chat/${encodeURIComponent(created.group.id)}/messages`, {
        method: 'POST',
        body: {
          content: draft.text,
          attachments,
          model_id: modelId === 'auto' ? undefined : modelId,
        },
      });
      if (!sent || sent.error) {
        await api(`/chat/${encodeURIComponent(created.group.id)}`, { method: 'DELETE' });
        throw new Error(sent.error || 'Could not send');
      }
      document.dispatchEvent(new CustomEvent('bonnie:chats'));
      navigate(withProduct(`/app/chat/${encodeURIComponent(created.group.id)}`, ctx.productId));
    } catch (err) {
      composer.setBusy(false);
      if (!preset) composer.restore(draft);
      toast((err && err.message) || 'Could not start the chat', 'error');
    }
  }

  function paint(integrations) {
    clear(body);
    const { groups, labels } = connectedGroups(integrations);
    const stages = filterStages(STAGES, groups);
    const prompts = filterSuggested(SUGGESTED, groups);
    const names = integrations.map((item) => item.provider_display || item.provider_name).filter(Boolean);
    const hero = el('div', { class: 'nc-hero rv' },
      el('span', { class: 'thread-chip' }, names.length
        ? `${names.length} live source${names.length === 1 ? '' : 's'} · ${names.join(' · ')}`
        : 'No live sources yet'),
      el('h1', { class: 'nc-title' }, 'One question. ', el('em', {}, 'The full story.')),
      el('p', { class: 'nc-lead' }, `Ask anything about ${ctx.productName || 'this product'} in plain English. Bonnie reads the sources you have connected.`));
    const stageBox = el('div', { class: 'stage-grid' });
    const openWith = el('div', { class: 'open-with' });
    const counter = el('span', {}, stages.length ? `01 / ${pad(stages.length)} · ${stages[0].name}` : '00 / 00');
    const stageSection = el('section', { class: 'stage-block rv', 'aria-labelledby': 'stage-heading' },
      el('div', { class: 'stage-label' },
        el('span', { id: 'stage-heading' }, 'Start with a growth stage'),
        counter),
      stageBox,
      openWith);
    const modelId = () => {
      const select = root.querySelector('.model-select');
      return (select && select.value) || 'auto';
    };
    body.append(hero, stageSection, composer.node, suggestedBlock(prompts, (question) => {
      send({ modelId: modelId() }, { text: question, files: [] });
    }));

    function choose(index) {
      const stage = stages[index];
      counter.textContent = `${pad(index + 1)} / ${pad(stages.length)} · ${stage.name}`;
      composer.setPlaceholder(stage.placeholder);
      for (const buttonNode of stageBox.querySelectorAll('.stage')) {
        const on = buttonNode.getAttribute('data-index') === String(index);
        buttonNode.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      clear(openWith);
      openWith.append(el('span', { class: 'open-kicker' }, '✦ Open with'));
      for (const question of stage.questions) {
        openWith.append(el('button', {
          type: 'button',
          class: 'starter',
          onClick: () => send({ modelId: modelId() }, { text: question, files: [] }),
        }, question, el('span', { class: 'spark', 'aria-hidden': 'true' }, ' →')));
      }
    }

    if (!stages.length) {
      stageSection.append(empty({
        title: 'Connect a source',
        body: 'Growth stages appear once an analytics, code, or revenue source is connected.',
        action: button('Open connectors', { href: withProduct('/app/connectors', ctx.productId), variant: 'primary' }),
      }));
      return;
    }
    stageBox.append(el('div', { class: 'stage-track', 'aria-hidden': 'true' }, el('i')));
    stages.forEach((stage, index) => {
      stageBox.append(el('button', {
        type: 'button',
        class: 'stage',
        'data-index': String(index),
        'aria-pressed': index === 0 ? 'true' : 'false',
        onClick: () => choose(index),
      },
      el('span', { class: 'stage-top' },
        el('span', { class: 'stage-num' }, pad(index + 1)),
        el('span', { class: 'stage-src' }, stageSources(stage, labels) || stage.groups.join(' · '))),
      el('span', { class: 'stage-name' }, stage.name),
      el('span', { class: 'stage-hint' }, stage.hint),
      el('span', { class: 'stage-bar', 'aria-hidden': 'true' },
        el('span', { style: `width:${stage.width}` }))));
    });
    choose(0);
  }

  async function load() {
    if (!ctx.productId) {
      clearTimeout(slowTimer);
      clear(body);
      body.append(errorPanel({
        title: 'Choose a product',
        message: 'New chats belong to a product.',
      }));
      return;
    }
    const data = await api(`/connectors?product_id=${encodeURIComponent(ctx.productId)}`);
    clearTimeout(slowTimer);
    if (!root.isConnected) return;
    if (!data || data.error) {
      clear(body);
      body.append(errorPanel({
        title: 'Sources unavailable',
        message: data.error || 'Connected sources could not be loaded.',
        action: button('Try again', { onClick: () => { body.replaceChildren(skeleton(4)); load(); } }),
      }));
      return;
    }
    paint(data.integrations || []);
  }

  load();
  root.addEventListener('bonnie:unmount', () => clearTimeout(slowTimer));
  return root;
}

function suggestedBlock(prompts, onAsk) {
  const row = el('div', { class: 'suggest-row' });
  for (const prompt of prompts) {
    row.append(el('button', {
      type: 'button',
      class: 'suggest',
      onClick: () => onAsk(prompt.question),
    },
    el('span', { class: 'suggest-src' }, prompt.label, el('span', { class: 'spark' }, ' →')),
    el('span', { class: 'suggest-q' }, prompt.question)));
  }
  return el('section', { class: 'suggest-block rv', 'aria-label': 'Suggested questions' },
    prompts.length ? el('div', { class: 'stage-label' }, 'Try asking') : null,
    row);
}

function pad(n) {
  return String(n).padStart(2, '0');
}
